import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../config/api';
import { clearBoardCaches } from '../lib/boardCache';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

// ── Storage helpers ────────────────────────────────────────────────────────────
// Auth data persists in localStorage so users stay logged in across browser
// sessions (closing the tab/browser does NOT log them out). The 3-hour
// inactivity timeout (App.js / useSessionTimeout) still applies for security.

const authSet   = (k, v) => localStorage.setItem(k, v);
const authGet   = (k)    => localStorage.getItem(k);
const authClear = (...keys) => keys.forEach(k => localStorage.removeItem(k));

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Restore persisted session from localStorage (survives tab/browser close)
    const token    = authGet('token');
    const savedUser = authGet('user');
    if (token && savedUser) {
      setUser(JSON.parse(savedUser));
    }
    setLoading(false);
  }, []);

  // Creates a usage-tracking session after the token is stored.
  const startSession = async () => {
    try {
      const res = await api.post('/sessions/start');
      if (res.data?.session_id) {
        authSet('session_id', res.data.session_id);
      }
    } catch { /* session tracking is non-critical */ }
  };

  const login = async (email, password) => {
    // Send the trusted-device token (if any) so a device that passed 2FA in the
    // last 24h isn't prompted again.
    const device_token = localStorage.getItem('trusted_device_token') || undefined;
    const response = await api.post('/auth/login', { email, password, device_token });
    if (response.data.requires_2fa) {
      return { requires_2fa: true, challenge_token: response.data.challenge_token };
    }
    const { access_token, user: userData } = response.data;
    authSet('token', access_token);
    authSet('user', JSON.stringify(userData));
    setUser(userData);
    await startSession();
    return userData;
  };

  const verify2FA = async (challengeToken, code) => {
    const response = await api.post('/auth/2fa/verify-challenge', {
      challenge_token: challengeToken,
      code,
    });
    const { access_token, user: userData, device_token } = response.data;
    authSet('token', access_token);
    authSet('user', JSON.stringify(userData));
    // Remember this device for 24h so 2FA isn't required again here.
    if (device_token) localStorage.setItem('trusted_device_token', device_token);
    setUser(userData);
    await startSession();
    return userData;
  };

  // Microsoft sign-in: `proof` is { id_token, nonce } from lib/microsoftLogin.
  const loginWithMicrosoft = async (proof) => {
    const response = await api.post('/auth/microsoft', proof);
    const { access_token, user: userData } = response.data;
    authSet('token', access_token);
    authSet('user', JSON.stringify(userData));
    setUser(userData);
    await startSession();
    return userData;
  };

  const register = async (email, password, name) => {
    const response = await api.post('/auth/register', { email, password, name });
    const { access_token, user: userData, requires_company_selection } = response.data;
    authSet('token', access_token);
    authSet('user', JSON.stringify(userData));
    setUser(userData);
    return { ...userData, requires_company_selection };
  };

  // Merge profile changes (name, avatar) into the live user + persisted copy.
  const updateUser = (patch) => {
    setUser((prev) => {
      const next = { ...(prev || {}), ...patch };
      authSet('user', JSON.stringify(next));
      return next;
    });
  };

  const logout = useCallback(async () => {
    const sessionId = authGet('session_id');
    if (sessionId) {
      try { await api.post(`/sessions/end/${sessionId}`); } catch { /* silent */ }
    }
    // Clear sensitive session data from sessionStorage
    authClear('token', 'user', 'session_id');
    // Clear workspace preference from localStorage (security: don't leak which workspace was open)
    localStorage.removeItem('last_workspace_id');
    // Clear cached board data so it doesn't leak to the next user on a shared machine
    clearBoardCaches();
    setUser(null);
  }, []);

  const sessionId = typeof window !== 'undefined' ? authGet('session_id') : null;

  return (
    <AuthContext.Provider value={{ user, login, verify2FA, loginWithMicrosoft, register, logout, loading, sessionId, updateUser }}>
      {children}
    </AuthContext.Provider>
  );
};
